import type { FC, FormEvent, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { PublicPageProps } from '@/types'

import Link from 'next/link'
import { useState } from 'react'
import { CheckCircle2, MailCheck } from 'lucide-react'
import { useI18n } from '@/i18n'
import { Seo } from '@/components/generals/seo'
import { Button, buttonClass } from '@/components/ui/button'
import { Field, Input, PasswordInput } from '@/components/ui/field'
import { SiteLayout } from '@/components/site/site-layout'
import { AuthShell } from '@/components/site/auth-shell'
import { apiRequest, errorCode, errorFields } from '@/lib/client/api'
import { loadPublicProps } from '@/lib/server/ssr'

type Step = 'request' | 'reset' | 'done'

const ForgotPasswordPage: FC<PublicPageProps> = (base): ReactNode => {
    const { t } = useI18n()
    const [step, setStep] = useState<Step>('request')
    const [email, setEmail] = useState('')
    const [code, setCode] = useState('')
    const [password, setPassword] = useState('')
    const [passwordConfirm, setPasswordConfirm] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

    const message = (code: string): string => (t.errors as Record<string, string>)[code] ?? t.errors.server_error

    const requestCode = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setError(null)

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) {
            setError(t.errors.invalid_email)

            return
        }

        setLoading(true)

        try {
            await apiRequest('/api/auth/forgot-password', { method: 'POST', body: { email } })
            setStep('reset')
        }
        catch (caught) {
            setError(message(errorCode(caught)))
        }
        finally {
            setLoading(false)
        }
    }

    const resetPassword = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setError(null)

        const found: Record<string, string> = {}

        if (code.replace(/\D/g, '').length !== 6) found.code = 'code_invalid'
        if (password.length < 8) found.password = 'password_short'
        if (passwordConfirm !== password) found.passwordConfirm = 'password_mismatch'

        setFieldErrors(found)

        if (Object.keys(found).length) return

        setLoading(true)

        try {
            await apiRequest('/api/auth/reset-password', { method: 'POST', body: { email, code, password, passwordConfirm } })
            setStep('done')
        }
        catch (caught) {
            setFieldErrors(errorFields(caught))
            setError(message(errorCode(caught)))
        }
        finally {
            setLoading(false)
        }
    }

    const fieldError = (key: string): string | null => (fieldErrors[key] ? message(fieldErrors[key]) : null)

    return (
        <SiteLayout {...base}>
            <Seo title={t.meta.forgotTitle} noIndex />

            <AuthShell
                title={t.auth.forgotTitle}
                text={step === 'request' ? t.auth.forgotText : undefined}
                footer={<Link href='/login' className='font-semibold text-navy underline underline-offset-2'>{t.auth.backToLogin}</Link>}
            >
                {step === 'request' && (
                    <form onSubmit={requestCode} noValidate className='flex flex-col gap-5'>
                        <Field label={t.auth.email} htmlFor='forgot-email'>
                            <Input id='forgot-email' type='email' autoComplete='email' value={email} onChange={event => setEmail(event.target.value)} />
                        </Field>

                        {error && <p role='alert' className='rounded-2xl bg-danger-soft px-4 py-3 text-sm font-medium text-destructive'>{error}</p>}

                        <Button type='submit' size='lg' loading={loading} className='w-full'>{t.auth.sendCode}</Button>
                    </form>
                )}

                {step === 'reset' && (
                    <form onSubmit={resetPassword} noValidate className='flex flex-col gap-5'>
                        <p role='status' className='flex gap-2 rounded-2xl bg-info-soft px-4 py-3 text-sm text-info'>
                            <MailCheck className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                            {t.auth.codeSent}
                        </p>

                        <Field label={t.auth.resetCode} htmlFor='reset-code' error={fieldError('code')}>
                            <Input
                                id='reset-code'
                                inputMode='numeric'
                                autoComplete='one-time-code'
                                maxLength={7}
                                dir='ltr'
                                value={code}
                                invalid={Boolean(fieldErrors.code)}
                                onChange={event => setCode(event.target.value.replace(/[^\d ]/g, ''))}
                                className='h-14 text-center font-mono text-2xl tracking-[0.5em]'
                                placeholder='000000'
                            />
                        </Field>

                        <Field label={t.auth.newPassword} htmlFor='reset-password' error={fieldError('password')} hint={t.auth.passwordHint}>
                            <PasswordInput
                                id='reset-password'
                                autoComplete='new-password'
                                value={password}
                                invalid={Boolean(fieldErrors.password)}
                                onChange={event => setPassword(event.target.value)}
                                showLabel={t.auth.showPassword}
                                hideLabel={t.auth.hidePassword}
                            />
                        </Field>

                        <Field label={t.auth.passwordConfirm} htmlFor='reset-passwordConfirm' error={fieldError('passwordConfirm')}>
                            <PasswordInput
                                id='reset-passwordConfirm'
                                autoComplete='new-password'
                                value={passwordConfirm}
                                invalid={Boolean(fieldErrors.passwordConfirm)}
                                onChange={event => setPasswordConfirm(event.target.value)}
                                showLabel={t.auth.showPassword}
                                hideLabel={t.auth.hidePassword}
                            />
                        </Field>

                        {error && <p role='alert' className='rounded-2xl bg-danger-soft px-4 py-3 text-sm font-medium text-destructive'>{error}</p>}

                        <Button type='submit' size='lg' loading={loading} className='w-full'>{t.auth.resetButton}</Button>
                    </form>
                )}

                {step === 'done' && (
                    <div role='status' className='flex flex-col items-start gap-4'>
                        <span className='flex size-14 items-center justify-center rounded-full bg-success-soft text-success'>
                            <CheckCircle2 className='size-7' aria-hidden='true' />
                        </span>
                        <p className='text-foreground/80'>{t.auth.resetDone}</p>
                        <Link href={`/login?email=${encodeURIComponent(email.trim())}`} className={buttonClass({ variant: 'primary' })}>{t.auth.loginButton}</Link>
                    </div>
                )}
            </AuthShell>
        </SiteLayout>
    )
}

export const getServerSideProps: GetServerSideProps<PublicPageProps> = async (ctx) => ({ props: await loadPublicProps(ctx) })

export default ForgotPasswordPage
